import React, { useEffect, useMemo, useRef, useState } from "react"

import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import {
    OneVerifyPermission,
    PermissionsExistenceLogicalOperator,
} from "@common-shared/config/rolesPermissions.config.ts"
import { IPolicyColumnConfiguration } from "@common-shared/types/policies.types.ts"

import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook.ts"

import { GENERAL_CONFIG } from "../../../config/general.config"
import {
    POLICIES_CONFIG,
    PolicyHeaderDragDirection,
} from "../../../config/policies.config"
import "../../../config/rolesPermissions.config"
import { ROUTES_CONFIG } from "../../../config/routes.config"
import { PoliciesHelper } from "../../../helpers/policies.helper"
import { PoliciesListHelper } from "../../../helpers/policiesList.helper"
import { useHasPermissions } from "../../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { usePoliciesList } from "../../../hooks/policiesList.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { PoliciesListComponent } from "../policiesList/policiesList.component"
import { PoliciesListHeaderComponent } from "../policiesList/policiesListHeader.component"
import { BulkPolicyControlComponent } from "../reports/bulkPolicyControl.component"
import { VisualizationWrapperComponent } from "../visualizationWrapper.component"
import { MedicaidActiveChecksVisualizationComponent } from "./medicaidActiveChecksVisualization.component"
import { MedicaidFlagsVisualizationComponent } from "./medicaidFlagsVisualization.component"

export const MedicaidReportsComponent: React.FunctionComponent = () => {
    useScrollFix()

    useHasPermissions(
        [OneVerifyPermission.viewHistoricalCoverageChecks],
        PermissionsExistenceLogicalOperator.OR,
    )

    useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_MEDICAID_REPORTS,
        undefined,
        ROUTES_CONFIG.profileUrl,
    )

    const {
        // vars
        canRunCoverageChecks,
        // state
        selectedPolicies,
        setSelectedPolicies,
        // functions
        onPoliciesSelected,
        onPoliciesDeselected,
        onDeselectAllPolicies,
    } = usePoliciesList()

    const scrollableTableRef = useRef<HTMLDivElement>(null)

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    // This component controls them and passes to list for display and header for modifications
    const [columnsConfiguration, setColumnsConfiguration] = useState<
        IPolicyColumnConfiguration[]
    >([])

    const listPoliciesSelectable = useMemo<boolean>(() => {
        return canRunCoverageChecks
    }, [canRunCoverageChecks])

    // Change columns configurations once practice role modalities change
    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        setColumnsConfiguration(
            PoliciesListHelper.getColumnsConfiguration(
                PoliciesListType.MEDICAID,
                selectedPracticeRole.availableModalities,
            ),
        )
    }, [selectedPracticeRole?.availableModalities])

    const onSaveColumnsConfiguration = (
        columns: IPolicyColumnConfiguration[],
    ) => {
        setColumnsConfiguration(columns)

        PoliciesListHelper.saveColumnsConfigurations(
            PoliciesListType.MEDICAID,
            columns,
            selectedPracticeRole?.availableModalities,
        )
    }

    const onChangeColumnOrder = (
        columnLabelMoveSource: string,
        columnLabelMoveTarget: string,
        direction: PolicyHeaderDragDirection,
    ) => {
        const newColumns = PoliciesListHelper.changeSingleColumnOrdering(
            columnsConfiguration,
            columnLabelMoveSource,
            columnLabelMoveTarget,
            direction,
        )

        onSaveColumnsConfiguration(newColumns)
    }

    return (
        <div className="pb-32 relative">
            <PageHelmetElement
                title={
                    POLICIES_CONFIG.headerTitleMapping[
                        PoliciesListType.MEDICAID
                    ]
                }
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            {selectedPracticeRole ? (
                <PoliciesListHeaderComponent
                    className="mb-[24px]"
                    listType={PoliciesListType.MEDICAID}
                    setSelectedPolicies={setSelectedPolicies}
                    scrollableTableRef={scrollableTableRef}
                    columnsConfiguration={columnsConfiguration}
                    onColumnsConfigurationChange={onSaveColumnsConfiguration}
                    withExport
                    withSavingFilterPresets
                />
            ) : (
                <SpinnerElement containerClassName="mt-[32px] text-center" />
            )}

            {!!selectedPracticeRole && (
                <VisualizationWrapperComponent
                    adjustHorizontalScrollReference={scrollableTableRef}
                    listType={PoliciesListType.MEDICAID}
                >
                    <MedicaidActiveChecksVisualizationComponent
                        className="
                            w-[350px] self-stretch
                            border-r border-solid border-brand-warmShadow
                        "
                    />

                    <MedicaidFlagsVisualizationComponent className="flex-1 self-stretch" />
                </VisualizationWrapperComponent>
            )}

            {selectedPracticeRole && columnsConfiguration.length ? (
                <PoliciesListComponent
                    scrollableElementRef={scrollableTableRef}
                    listType={PoliciesListType.MEDICAID}
                    sortEnabled={true}
                    columns={columnsConfiguration}
                    policiesSelectable={listPoliciesSelectable}
                    selectedItems={selectedPolicies}
                    onSelectedItems={onPoliciesSelected}
                    onDeselectedItems={onPoliciesDeselected}
                    onDeselectedAllItems={onDeselectAllPolicies}
                    onChangeColumnOrder={onChangeColumnOrder}
                    isSinglePolicySelectable={
                        PoliciesHelper.isPolicyRecheckEligible
                    }
                />
            ) : null}

            {listPoliciesSelectable && (
                <BulkPolicyControlComponent
                    listType={PoliciesListType.MEDICAID}
                    onCancelBulkPolicyControl={onDeselectAllPolicies}
                    coverageChecks={selectedPolicies}
                    setCoverageChecks={setSelectedPolicies}
                    quotaUsageType={QuotaUsageType.MEDICAID}
                    withBulkRecheck={canRunCoverageChecks}
                    bulkRecheckLabel="Check Medicaid"
                />
            )}
        </div>
    )
}
